import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Mail, Check, Loader2 } from "lucide-react";
import { useState } from "react";
import { CalendlyModal, CALENDLY_URLS } from "@/components/CalendlyModal";
import { useTheme } from "@/context/ThemeContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_birth-ready/artifacts/p71eeafq_tree%20blue%20.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Footer() {
  const location = useLocation();
  const { colors } = useTheme();
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
  const [isCalendlyOpen, setIsCalendlyOpen] = useState(false);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${API_URL}/api/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          email, 
          source: "footer",
          funnel: window.location.pathname.includes("leadership") ? "leadership" : "general"
        }),
      });

      if (response.ok) {
        setSubmitStatus("success");
        setEmail("");
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer data-testid="main-footer" className="transition-colors duration-300" style={{ backgroundColor: colors.warmStone }} role="contentinfo" aria-label="Site footer">
      {/* Hairline divider */}
      <div className="h-px transition-colors duration-300" style={{ backgroundColor: colors.border }} aria-hidden="true" />
      
      {/* Email Signup Section */}
      <div className="transition-colors duration-300" style={{ backgroundColor: colors.warmStoneHover }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 id="newsletter-heading" className="font-serif text-xl mb-2" style={{ color: colors.inkGrey }}>
                Stay Connected
              </h3>
              <p className="text-[#1c1510]/70 text-sm">
                Receive occasional insights on readiness, presence, and navigating pressure.
              </p>
            </div>
            
            <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-3 w-full md:w-auto" aria-labelledby="newsletter-heading">
              <div className="relative">
                <Mail 
                  size={18} 
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-[#1c1510]/40"
                  aria-hidden="true"
                />
                <label htmlFor="footer-email" className="sr-only">Email address for newsletter subscription</label>
                <input
                  id="footer-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  className="w-full md:w-72 pl-11 pr-4 py-3 text-sm bg-white border border-[rgba(28,21,16,0.18)] focus:border-[#a07830] focus:outline-none transition-colors"
                  data-testid="footer-email-input"
                  disabled={isSubmitting || submitStatus === "success"}
                  aria-describedby={submitStatus === "error" ? "subscribe-error" : undefined}
                  required
                />
              </div>
              
              <Button
                type="submit"
                disabled={isSubmitting || submitStatus === "success" || !email}
                className="rounded-full px-6 py-3 text-[13px] tracking-wide font-medium transition-all duration-300 flex items-center justify-center gap-2 min-w-[120px]"
                style={{ 
                  backgroundColor: submitStatus === "success" ? colors.oliveSage : colors.inkNavy, 
                  color: colors.softWhite,
                  opacity: (isSubmitting || !email) && submitStatus !== "success" ? 0.7 : 1
                }}
                data-testid="footer-subscribe-btn"
                aria-label={isSubmitting ? "Subscribing to newsletter" : submitStatus === "success" ? "Successfully subscribed" : "Subscribe to newsletter"}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" aria-hidden="true" />
                    Sending...
                  </>
                ) : submitStatus === "success" ? (
                  <>
                    <Check size={16} aria-hidden="true" />
                    Subscribed
                  </>
                ) : (
                  "Subscribe"
                )}
              </Button>
            </form>
          </div>
          
          {submitStatus === "error" && (
            <p id="subscribe-error" className="text-red-600 text-sm mt-3 text-center md:text-right" role="alert">
              Something went wrong. Please try again.
            </p>
          )}
        </div>
      </div>
      
      {/* Main Footer Content */}
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-24">
        
        {/* Brand & Contact Section - Centered Above Columns */}
        <div className="mb-16 pb-12 border-b border-[rgba(28,21,16,0.18)]/30 text-center">
          <div className="flex flex-col items-center gap-3 mb-4">
            <img
              src={LOGO_URL}
              alt="Innate Perspectives tree logo representing growth, grounding, and natural leadership development"
              className="w-14 h-14"
              style={{ 
                filter: 'brightness(0) saturate(100%)',
                opacity: 0.55
              }}
             loading="lazy" />
            <h3 className="text-xl text-[#1c1510]" style={{ fontFamily: "'Source Serif 4', serif", fontWeight: 500 }}>
              Innate Perspectives
            </h3>
          </div>
          <address className="not-italic">
            <p className="text-[#1c1510]/70 text-[15px] leading-relaxed mb-4">
              Sunshine Coast, Australia
              <br />
              Working Globally via Zoom
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6">
              <a 
                href="mailto:hello@innateperspectives.com" 
                className="text-[#a07830] text-[15px] hover:text-[#8a6628] transition-colors"
                aria-label="Email Innate Perspectives at hello@innateperspectives.com"
              >
                hello@innateperspectives.com
              </a>
              <span className="hidden sm:inline text-[rgba(28,21,16,0.18)]" aria-hidden="true">|</span>
              <a 
                href="tel:+61404065064" 
                className="text-[#a07830] text-[15px] hover:text-[#8a6628] transition-colors"
                aria-label="Call Innate Perspectives at +61 404 065 064"
              >
                +61 404 065 064
              </a>
            </div>
          </address>
        </div>
        
        {/* 4 Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-14 lg:gap-10">
          
          {/* Column 1: Executive Capacity Recalibration */}
          <div className="space-y-5">
            <h4 className="text-[15px] tracking-wide font-medium text-[#1c1510]">
              Capacity Recalibration
            </h4>
            <nav className="space-y-2.5">
              <Link
                to="/sage"
                className="block text-[#a07830] hover:text-[#8a6628] transition-colors text-[15px] font-medium"
                data-testid="footer-link-sage"
              >
                Sage. $127 CustomGPT
              </Link>
              <Link
                to="/leadership"
                className="block text-[#a07830] hover:text-[#8a6628] transition-colors text-[15px]"
              >
                Overview
              </Link>
              <Link
                to="/leadership/about"
                className="block text-[#a07830] hover:text-[#8a6628] transition-colors text-[15px]"
              >
                The Work
              </Link>
              <Link
                to="/the-framework"
                className="block text-[#a07830] hover:text-[#8a6628] transition-colors text-[15px]"
                data-testid="footer-link-framework"
              >
                The Framework
              </Link>
              <Link
                to="/leadership/services"
                className="block text-[#a07830] hover:text-[#8a6628] transition-colors text-[15px]"
              >
                Services
              </Link>
              <div className="pl-3 space-y-2">
                <Link
                  to="/leadership/services/focused-session"
                  className="block text-[#a07830]/80 hover:text-[#8a6628] transition-colors text-[14px]"
                >
                  Focused Stress Session
                </Link>
                <Link
                  to="/leadership/services/individual-session"
                  className="block text-[#a07830]/80 hover:text-[#8a6628] transition-colors text-[14px]"
                >
                  Single Session
                </Link>
                <Link
                  to="/leadership/services/10-session-program"
                  className="block text-[#a07830]/80 hover:text-[#8a6628] transition-colors text-[14px]"
                >
                  10-Session Series
                </Link>
                <Link
                  to="/leadership/services/12-month-immersion"
                  className="block text-[#a07830]/80 hover:text-[#8a6628] transition-colors text-[14px]"
                >
                  Leadership Stability Structure
                </Link>
                <Link
                  to="/leadership/scaling"
                  className="block text-[#a07830]/80 hover:text-[#8a6628] transition-colors text-[14px]"
                >
                  Scaling Leadership
                </Link>
              </div>
              <Link
                to="/leadership/faq"
                className="block text-[#a07830] hover:text-[#8a6628] transition-colors text-[15px]"
              >
                FAQ
              </Link>
            </nav>
          </div>

          {/* Column 2: Small Business & Evidence */}
          <div className="space-y-5">
            <h4 className="text-[15px] tracking-wide font-medium text-[#1c1510]">
              Small Business
            </h4>
            <nav className="space-y-2.5">
              <Link
                to="/smallbusiness"
                className="block text-[#a07830] hover:text-[#8a6628] transition-colors text-[15px]"
                data-testid="footer-link-smallbusiness"
              >
                For Self-Made Founders
              </Link>
              <p className="text-[13px] text-[#1c1510]/60 leading-relaxed">
                A 30-min Clarity Call · not a pitch
              </p>
            </nav>

            <h4 className="text-[15px] tracking-wide font-medium text-[#1c1510] pt-2">
              Evidence Base
            </h4>
            <nav>
              <Link
                to="/evidence"
                className="block text-[#a07830] hover:text-[#8a6628] transition-colors text-[15px]"
                data-testid="footer-link-evidence"
              >
                Research & References
              </Link>
            </nav>
          </div>

          {/* Column 3: Framework & Blog */}
          <div className="space-y-5">
            <h4 className="text-[15px] tracking-wide font-medium text-[#1c1510]">
              Framework & Blog
            </h4>
            <nav className="space-y-2.5">
              <Link
                to="/the-framework"
                className="block text-[#a07830] hover:text-[#8a6628] transition-colors text-[15px]"
                data-testid="footer-framework-overview"
              >
                Framework Overview
              </Link>
              <Link
                to="/blog"
                className="block text-[#a07830] hover:text-[#8a6628] transition-colors text-[15px]"
              >
                Blog
              </Link>
              <Link
                to="/blog/decision-readiness-under-pressure"
                className="block text-[#a07830] hover:text-[#8a6628] transition-colors text-[15px]"
              >
                Decision Readiness Under Pressure
              </Link>
              <div className="pl-3 space-y-2 pt-1">
                <Link
                  to="/blog/category/decision-under-pressure"
                  className="block text-[#a07830]/80 hover:text-[#8a6628] transition-colors text-[14px]"
                >
                  Decision Under Pressure
                </Link>
                <Link
                  to="/blog/category/conative-action"
                  className="block text-[#a07830]/80 hover:text-[#8a6628] transition-colors text-[14px]"
                >
                  Conative Action
                </Link>
                <Link
                  to="/blog/category/beliefs-and-protective-patterns"
                  className="block text-[#a07830]/80 hover:text-[#8a6628] transition-colors text-[14px]"
                >
                  Beliefs & Patterns
                </Link>
              </div>
            </nav>
          </div>

          {/* Column 4: Begin Here - More space */}
          <div className="space-y-6">
            <h4 className="text-[15px] tracking-wide font-medium text-[#1c1510]">
              Begin Here
            </h4>
            <p className="text-[#1c1510]/70 text-[15px] leading-relaxed">
              If you&apos;re curious about whether this work resonates, a Clarity Call is a simple way to begin.
            </p>
            <p className="text-[#1c1510]/70 text-[15px] leading-relaxed">
              No preparation needed. Just a conversation.
            </p>
            <Button 
              onClick={() => setIsCalendlyOpen(true)}
              data-testid="footer-clarity-call-btn"
              className="rounded-full px-8 py-3 text-[14px] font-medium tracking-wide transition-all duration-300 flex items-center gap-2 mt-2"
              style={{ backgroundColor: colors.inkNavy, color: colors.softWhite }}
            >
              Book a Clarity Call
              <ArrowRight size={16} />
            </Button>
          </div>
        </div>
      </div>

      {/* Calendly Modal */}
      <CalendlyModal 
        isOpen={isCalendlyOpen} 
        onClose={() => setIsCalendlyOpen(false)} 
        calendlyUrl={CALENDLY_URLS.clarityCall}
        title="Book a Clarity Call"
      />

      {/* Canonical Entity Description */}
      <div className="bg-[#faf7f2]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-8">
          <p className="text-[#1c1510]/70 text-[14px] leading-relaxed max-w-4xl">
            Decision readiness under pressure, aligning instinctive action, behavioural access and subconscious safety predictions so behaviour remains self-directed in critical moments.
          </p>
        </div>
      </div>

      {/* Legal & Scope Section */}
      <div className="bg-[#ede8de]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-10">
          <div className="space-y-4">
            <h4 className="text-[13px] tracking-wide font-medium text-[#1c1510]/70">
              Legal & Scope
            </h4>
            <p className="text-[#1c1510]/60 text-[14px] leading-relaxed max-w-4xl">
              Innate Perspectives™ provides non-medical, non-clinical services delivered from Queensland, Australia. This work supports internal readiness and decision clarity and does not replace medical, mental health, or professional financial advice. Services are governed by Queensland law.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Bar - Extra padding for sticky CTA */}
      <div className="bg-[#faf7f2] pb-24">
        <div className="h-px bg-[rgba(28,21,16,0.18)]/50" />
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Legal Links */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-[#1c1510]">
              <Link to="/legal/privacy-policy" className="hover:text-[#1c1510]/70 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/legal/terms-and-conditions" className="hover:text-[#1c1510]/70 transition-colors">
                Terms & Conditions
              </Link>
              <Link to="/legal/disclaimer" className="hover:text-[#1c1510]/70 transition-colors">
                Disclaimer
              </Link>
              <Link to="/legal/informed-consent" className="hover:text-[#1c1510]/70 transition-colors">
                Informed Consent
              </Link>
              <Link to="/legal/refund-policy" className="hover:text-[#1c1510]/70 transition-colors">
                Refund Policy
              </Link>
              <Link to="/legal/cookies-policy" className="hover:text-[#1c1510]/70 transition-colors">
                Cookies
              </Link>
              <Link to="/legal/accessibility" className="hover:text-[#1c1510]/70 transition-colors">
                Accessibility
              </Link>
            </div>

            {/* Copyright */}
            <p className="text-[13px] text-[#1c1510]/50">
              © {currentYear} Innate Perspectives. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
